import React, { useState, useEffect, useCallback } from "react";
import { useSocialCoachSession, getErrorMessage } from "@/utils";
import {
  CoachSocialNetworkProfile,
  CoachSocialNetworkProfileServices,
  SocialNetworkProfileServices,
} from "@/services/bedrock";
import { SocialNetworkIcon } from "@/components";
import { socialNetworkNames } from "@/interfaces";
import { Button, Dialog, Table, Spinner } from "@/components/ui";
import { Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export interface ActiveConnectionsDialogProps {
  profile: CoachSocialNetworkProfile;
  onClose: () => void;
}

export function ActiveConnectionsDialog({ profile, onClose }: ActiveConnectionsDialogProps) {
  const { coachId } = useSocialCoachSession();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [connections, setConnections] = useState<CoachSocialNetworkProfile[]>([]);
  const [disconnectingChildId, setDisconnectingChildId] = useState<string | number | null>(null);

  const loadConnections = useCallback(async () => {
    if (!coachId) return;
    setLoading(true);
    try {
      const response = await CoachSocialNetworkProfileServices.getActiveChildProfiles(coachId, profile.id, page, 8);
      setConnections(response.content || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load active connections list.");
    } finally {
      setLoading(false);
    }
  }, [coachId, profile.id, page]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleDisconnectChild = async (childProfile: CoachSocialNetworkProfile) => {
    const childName = childProfile.socialIdentity?.displayName || "this profile";
    if (!window.confirm(`Are you sure you want to disconnect player child connection: "${childName}"?`)) return;

    setDisconnectingChildId(childProfile.id);
    try {
      // Deleting a child connection uses standard user-level disconnect profile endpoint
      await SocialNetworkProfileServices.deleteProfile(childProfile.socialIdentity.id, childProfile.id);
      toast.success(`Disconnected child profile connection: "${childName}".`);
      loadConnections();
    } catch (e) {
      console.error(e);
      toast.error("Failed to disconnect player child profile", {
        description: getErrorMessage(e),
      });
    } finally {
      setDisconnectingChildId(null);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()} size="xl" showCloseButton>
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2">
          <SocialNetworkIcon socialNetworkId={profile.socialNetworkId} size="mini" />
          Active Player Connections
        </Dialog.Title>
        <Dialog.Description>
          View all player child profiles assigned under this parent {socialNetworkNames[profile.socialNetworkId]}{" "}
          connection.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="min-h-[300px]">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner className="size-8" />
          </div>
        ) : connections.length === 0 ? (
          <div className="text-center py-16 flex flex-col items-center justify-center gap-2">
            <Users className="size-10 text-muted-foreground opacity-50" />
            <p className="text-sm font-semibold text-muted-foreground m-0">No active player connections found.</p>
            <p className="text-xs text-muted-foreground max-w-sm m-0">
              Click &apos;Bulk Assign&apos; on the parent card to link connections or locations to your players.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head className="font-bold">Player Name</Table.Head>
                    <Table.Head className="font-bold">Sub-Account / Connection Name</Table.Head>
                    <Table.Head className="font-bold text-center">Status</Table.Head>
                    <Table.Head className="font-bold text-right">Actions</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {connections.map(conn => (
                    <Table.Row key={`child-conn-${conn.id}`} className="hover:bg-gray-50/50">
                      <Table.Cell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm text-foreground">{conn.profile?.fullName || "-"}</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm text-foreground">
                            {conn.socialIdentity?.username || conn.socialIdentity?.displayName || "Connection"}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold ${
                            conn.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          <span className={`size-1.5 rounded-full ${conn.isActive ? "bg-green-500" : "bg-gray-400"}`} />
                          {conn.isActive ? "Active" : "Inactive"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => handleDisconnectChild(conn)}
                          disabled={disconnectingChildId === conn.id}
                        >
                          {disconnectingChildId === conn.id ? (
                            <Spinner className="size-3" />
                          ) : (
                            <Trash2 className="size-4" />
                          )}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-end items-center gap-2 mt-2">
                <Button variant="outline" size="xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button variant="outline" size="xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer>
        <Button onClick={onClose}>Close</Button>
      </Dialog.Footer>
    </Dialog>
  );
}
